import store from "#components/Redux/store";
import Routers from "#src/Routers";
import history from "#src/Routers/history";
import { UnknownErrorLayout } from "#src/batteries-included-components/Layouts/Errors/UnknownError";
import { DevTools } from "#src/components/_development/DevTools";
import { AuthenticatedContextProvider } from "#src/contexts/AuthenticatedContext";
import { CustomAppRegistryProvider } from "#src/contexts/CustomAppRegistryContext";
import { DefaultUserInterfaceConfigurationProvider } from "#src/contexts/DefaultUserInterfaceConfigurationContext";
import { MeasurementTypeProvider } from "#src/contexts/MeasurementTypeContext";
import LocalizationProvider from "#src/hooks/LocalizationProvider";
import { ExceptionUtils } from "#src/utils/exception";
import { ErrorBoundary } from "@sentry/react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { Button, Toaster, useToast } from "@validereinc/common-components";
import { normalizeError } from "@validereinc/domain-controllers";
import { HTTPRequestError, HTTPResponseError } from "@validereinc/utilities";
import "airbnb-js-shims";
import { History } from "history";
import React, { useEffect, useRef } from "react";
import { hot } from "react-hot-loader";
import { Provider } from "react-redux";
import { Titled } from "react-titled";

// IMPROVE: better typing
const AppContent = ({ store, history }: { store: any; history: History }) => {
  const { toast } = useToast();
  const queryClient = useRef<QueryClient>(
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: (failureCount, err) => {
            // custom errors have appropriate UI likely shown, so we won't be
            // retrying after the first request
            if (
              err instanceof HTTPResponseError ||
              err instanceof HTTPRequestError
            ) {
              return false;
            }

            return failureCount <= 3;
          },
          refetchOnWindowFocus: (q) => {
            // no refetching on window focus change will occur when error is a
            // custom error (as appropriate UI is likely shown and we don't want
            // that to change until the user acts and likely navigates away)
            if (
              q.state.error instanceof HTTPResponseError ||
              q.state.error instanceof HTTPRequestError
            ) {
              return false;
            }

            // https://validere.atlassian.net/browse/CHB-2614
            // Users' use our app between tabs, and the refreshOnWindowFocus
            // causes a lot of unnecessary loading and waiting
            // Enable this on a case-by-case basis
            return false;
          },
        },
      },
      queryCache: new QueryCache({
        onError: (err, query) => {
          const normalizedErr = normalizeError(err, { wrapHTTPErrors: true });

          ExceptionUtils.reportException(
            // log original error intentionally, but severity is normalized
            err,
            normalizedErr.severity,
            {
              hint: `Captured by react query global query cache onError handler (query key: ${JSON.stringify(query.queryKey)})`,
            }
          );

          if (query.meta?.hideErrorToasts) {
            return;
          }

          if ([404, 401, 403].includes(normalizedErr.code)) {
            if (normalizedErr.code === 403 || normalizedErr.code === 401) {
              toast.push({
                intent: "warning",
                description: normalizedErr.getExplanation(query.meta?.errorCtx),
              });
            }

            return;
          }

          // the UI should handle the remaining query errors, but this is here as a fallback
          // TODO: enable when messaging is clearer everywhere
          // toast.push({
          //   intent: "error",
          //   description: normalizedErr.getExplanation(query.meta?.errorCtx),
          // });
          return;
        },
      }),
      mutationCache: new MutationCache({
        onError: (err, _, __, mtx) => {
          const normalizedErr = normalizeError(err, { wrapHTTPErrors: true });

          ExceptionUtils.reportException(
            // log original error intentionally, but severity is normalized
            err,
            normalizedErr.severity,
            {
              hint: "Captured by react query global mutation cache onError handler",
            }
          );

          if (mtx.meta?.hideErrorToasts) {
            return;
          }

          if ([404, 401, 403].includes(normalizedErr.code)) {
            if (normalizedErr.code === 403 || normalizedErr.code === 401) {
              toast.push({
                intent: "warning",
                description: normalizedErr.getExplanation(mtx.meta?.errorCtx),
              });
            }

            return;
          }

          // TODO: other mutation errors should be handled at source but till we
          // refactor all instances, we need to cover default behavior here
          toast.push({
            intent: "error",
            description: normalizedErr.getExplanation(mtx.meta?.errorCtx),
          });
          return;
        },
      }),
    })
  );

  useEffect(() => {
    ExceptionUtils.registerLifecycleEvent({
      category: "app",
      level: "info",
      type: "info",
      message: "Initialized query client and app shell",
    });
  }, []);

  if (!queryClient.current) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient.current}>
      <Provider store={store}>
        <AuthenticatedContextProvider>
          <MeasurementTypeProvider>
            <CustomAppRegistryProvider>
              <LocalizationProvider>
                <DefaultUserInterfaceConfigurationProvider>
                  <Titled title="Validere">
                    <Routers history={history} />
                  </Titled>
                </DefaultUserInterfaceConfigurationProvider>
              </LocalizationProvider>
            </CustomAppRegistryProvider>
          </MeasurementTypeProvider>
        </AuthenticatedContextProvider>
      </Provider>
      {process.env.NODE_ENV === "development" ? <DevTools /> : null}
    </QueryClientProvider>
  );
};

const App = () => (
  <ErrorBoundary
    beforeCapture={(scope) => {
      scope.setTag(
        ExceptionUtils.ExceptionContextKeys.isAppShellFatalFailure,
        true
      );
    }}
    fallback={({ error }) => (
      <UnknownErrorLayout
        error={error}
        action={[
          <Button
            key="go-back"
            variant="primary"
            onClick={() => {
              history.goBack();
            }}
          >
            Go Back
          </Button>,
          <Button
            key="reload"
            style={{ marginLeft: "8px" }}
            onClick={() => window.location.reload()}
          >
            Reload Page
          </Button>,
        ]}
      />
    )}
  >
    <>
      <AppContent
        store={store}
        history={history}
      />
      <Toaster
        position="bottom-right"
        visibleToasts={5}
        expand
      />
    </>
  </ErrorBoundary>
);

export default hot(module)(App);
